import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { formatTRY } from "@/lib/api";
import { Trash2, TrendingUp, TrendingDown, HandCoins } from "lucide-react";

export const TX_TYPES = [
  { value: "gelir", label: "Gelir", icon: TrendingUp, color: "text-emerald-600", bg: "bg-emerald-100" },
  { value: "gider", label: "Gider", icon: TrendingDown, color: "text-red-600", bg: "bg-red-100" },
  { value: "tahsilat", label: "Tahsilat", icon: HandCoins, color: "text-indigo-600", bg: "bg-indigo-100" },
];

export const FinanceSummary = ({ totals }) => (
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
    {TX_TYPES.map((t) => (
      <Card key={t.value} className="p-5 border-slate-200">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">{t.label}</div>
            <div className="text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Outfit" }}>{formatTRY(totals[t.value] || 0)}</div>
          </div>
          <div className={`h-11 w-11 rounded-xl ${t.bg} flex items-center justify-center ${t.color}`}><t.icon className="h-5 w-5" /></div>
        </div>
      </Card>
    ))}
  </div>
);

export const TransactionForm = ({ form, setForm, onSubmit }) => {
  const set = (patch) => setForm({ ...form, ...patch });
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <Label>Tür *</Label>
        <Select value={form.type} onValueChange={(v) => set({ type: v })}>
          <SelectTrigger data-testid="transaction-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>{TX_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div><Label>Tutar ₺ *</Label><Input type="number" step="0.01" required value={form.amount} onChange={(e) => set({ amount: e.target.value })} data-testid="transaction-amount-input" /></div>
      <div><Label>Tarih *</Label><Input type="date" required value={form.date} onChange={(e) => set({ date: e.target.value })} data-testid="transaction-date-input" /></div>
      <div><Label>Kategori (Örn: Malzeme, Yakıt, Fatura)</Label><Input value={form.category} onChange={(e) => set({ category: e.target.value })} data-testid="transaction-category-input" /></div>
      <div><Label>Açıklama</Label><Textarea rows={2} value={form.description} onChange={(e) => set({ description: e.target.value })} /></div>
      <DialogFooter><Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-transaction-button">Kaydet</Button></DialogFooter>
    </form>
  );
};

export const TransactionsTable = ({ items, onDelete }) => (
  <Card className="border-slate-200 overflow-hidden" data-testid="collections-table">
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
          <tr>
            <th className="text-left p-4 font-medium">Tarih</th>
            <th className="text-left p-4 font-medium">Tür</th>
            <th className="text-left p-4 font-medium">Kategori</th>
            <th className="text-left p-4 font-medium">Açıklama</th>
            <th className="text-right p-4 font-medium">Tutar</th>
            <th className="text-right p-4 font-medium">İşlem</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200">
          {items.length === 0 && <tr><td colSpan={6} className="text-center py-12 text-slate-400">Kayıt yok</td></tr>}
          {items.map((t) => {
            const info = TX_TYPES.find((x) => x.value === t.type);
            const isExpense = t.type === "gider";
            return (
              <tr key={t.id} className="hover:bg-slate-50">
                <td className="p-4 font-mono text-xs text-slate-600">{new Date(t.date).toLocaleDateString("tr-TR")}</td>
                <td className="p-4"><Badge variant="outline" className={`${info?.color} border-current`}>{info?.label}</Badge></td>
                <td className="p-4 text-slate-700">{t.category || "-"}</td>
                <td className="p-4 text-slate-600 max-w-xs truncate">{t.description || "-"}</td>
                <td className={`p-4 text-right font-bold ${isExpense ? "text-red-600" : "text-emerald-700"}`}>{isExpense ? "-" : "+"}{formatTRY(t.amount)}</td>
                <td className="p-4 text-right">
                  <Button size="sm" variant="ghost" onClick={() => onDelete(t.id)} data-testid={`delete-tx-${t.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </Card>
);
